import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../api/axios';
import logo from '../wysupp-icon-green-transparent.svg';
import AccountInfo from '../AccountInfo';
import { FaEdit, FaPlus, FaArrowLeft } from 'react-icons/fa';

export default function EditProjectListPage() {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    const fetchProjects = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await axiosInstance.get('/api/projects/');
            setProjects(response.data || []);
            setLoading(false); // Stop loading on success
        } catch (err) {
            // --- TIMEOUT FIX: Prevent red error flash on timeout ---
            if (err.response?.status === 401) return;

            setError(err.response?.data?.detail || 'Failed to fetch projects.');
            setLoading(false); // Stop loading on actual errors
        }
    }, []);

    useEffect(() => {
        fetchProjects();
    }, [fetchProjects]);

    return (
        <div className="admin-page-container">
            <header className="dashboard-header">
                <Link to="/home" style={{ textDecoration: 'none' }}>
                    <div className="logo-section" style={{ cursor: 'pointer' }}>
                        <img src={logo} className="logo-icon" alt="Wysupp Logo" />
                        <span className="logo-text">Wysupp Validate</span>
                        <span className="logo-subtext">Data Quality Management</span>
                    </div>
                </Link>
                <AccountInfo />
            </header>
            <main className="admin-main-content">
                <div className="page-header justify-between">
                    <div className="page-header-left">
                        <button onClick={() => navigate('/admin/manage-projects')} className="back-button">
                            <FaArrowLeft /> Back
                        </button>
                        <h1 className="page-title">Select Project to Edit</h1>
                    </div>
                    <button
                        onClick={() => navigate('/admin/manage-projects/create-project')}
                        className="action-button create-button"
                    >
                        <FaPlus /> Create New Project
                    </button>
                </div>

                <div className="table-wrapper">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Project Name</th>
                                <th>Project Users</th>
                                <th>Status</th>
                                <th className="actions-column">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan="4" className="table-message">Loading projects...</td></tr>}
                            {error && <tr><td colSpan="4" className="table-message error">{error}</td></tr>}
                            {!loading && !error && projects.length === 0 && (
                                <tr>
                                    <td colSpan="4" className="table-message">No projects found.</td>
                                </tr>
                            )}
                            {!loading && !error && projects.map(project => (
                                <tr key={project.id}>
                                    <td data-label="Project Name">{project.project_name}</td>
                                    <td data-label="Project Users">
                                        {project.project_users && project.project_users.length > 0
                                            ? project.project_users.map(pu => pu.username).join(', ')
                                            : 'No users in project'}
                                    </td>
                                    <td data-label="Status">
                                        <span className={`status-badge active`}>
                                            Active
                                        </span>
                                    </td>
                                    <td data-label="Actions" className="actions-column">
                                        <button
                                            onClick={() => navigate(`/admin/manage-projects/edit-project/${project.id}`)}
                                            className="action-button edit-button"
                                        >
                                            <FaEdit /> Edit
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </main>
            <style>{`
                :root {
                    --dark-bg: #1a202c; --card-bg: #2d3748; --text-color: #e2e8f0; --light-text: #a0aec0;
                    --green-accent: #48bb78; --border-color: #4a5568; --input-bg: #1a202c;
                }
                .admin-page-container {
                    background-color: var(--dark-bg);
                    min-height: 100vh;
                    color: var(--text-color);
                    font-family: 'Inter', sans-serif;
                }
                .dashboard-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1rem 3rem;
                    background-color: var(--card-bg);
                    border-bottom: 1px solid var(--border-color);
                }
                .logo-section { display: flex; align-items: center; gap: 0.5rem; }
                .logo-icon { width: 30px; height: 30px; }
                .logo-text { font-size: 1.25rem; font-weight: 700; color: var(--green-accent); }
                .logo-subtext { font-size: 0.75rem; color: var(--light-text); margin-left: 0.5rem; padding-left: 0.5rem; border-left: 1px solid var(--light-text); }
                .admin-main-content {
                    max-width: 1400px;
                    margin: 2rem auto;
                    padding: 0 2rem;
                }
                .page-header {
                    display: flex;
                    align-items: center;
                    margin-bottom: 2rem;
                }
                .page-header.justify-between {
                    justify-content: space-between;
                }
                .page-header-left {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                }
                .back-button {
                    background: var(--card-bg); color: var(--text-color); border: 1px solid var(--border-color);
                    padding: 0.5rem 1rem; border-radius: 6px; font-weight: 600; cursor: pointer;
                    transition: background-color 0.2s; display: flex; align-items: center; gap: 0.5rem;
                }
                .back-button:hover { background: #4a5568; }
                .page-title {
                    font-size: 2rem;
                    font-weight: 700;
                    color: var(--text-color);
                }

                .action-button {
                    padding: 0.6rem 1.2rem; border: none; border-radius: 6px;
                    font-weight: 600; cursor: pointer; transition: all 0.2s;
                    display: flex; align-items: center; gap: 0.5rem;
                }

                .create-button {
                    background-color: var(--green-accent);
                    color: var(--dark-bg);
                }
                .create-button:hover {
                    background-color: #38a169;
                }

                .table-wrapper { overflow-x: auto; background-color: var(--card-bg); border: 1px solid var(--border-color); border-radius: 12px; }
                .admin-table { width: 100%; border-collapse: collapse; }
                .admin-table th, .admin-table td {
                    padding: 1rem 1.25rem; text-align: left;
                    white-space: normal; word-break: break-word;
                }
                .admin-table thead {  }
                .admin-table th {
                    background-color: #1a202c;
                    font-size: 0.75rem; font-weight: 600; color: var(--light-text);
                    text-transform: uppercase; letter-spacing: 0.05em;
                }
                .admin-table th:first-child { border-top-left-radius: 12px; }
                .admin-table th:last-child { border-top-right-radius: 12px; }
                .admin-table tbody tr { border-top: 1px solid var(--border-color); }
                .admin-table tbody tr:hover { background-color: #374151; }
                .actions-column { text-align: right; }
                .edit-button { background-color: #3b82f6; color: white; font-size: 0.875rem; padding: 0.5rem 1rem; }
                .edit-button:hover { background-color: #2563eb; }
                .status-badge { padding: 0.25rem 0.75rem; border-radius: 99px; font-size: 0.75rem; font-weight: 600; }
                .status-badge.active { background-color: rgba(72, 187, 120, 0.2); color: #6ee7b7; }
                .status-badge.inactive { background-color: rgba(107, 114, 128, 0.2); color: #9ca3af; }
                .table-message { text-align: center; padding: 2rem; color: var(--light-text); font-size: 1.1rem; }
                .table-message.error { color: #fca5a5; font-weight: 600; }
                
                @media (max-width: 900px) {
                    .admin-table thead { display: none; }
                    .admin-table, .admin-table tbody, .admin-table tr, .admin-table td { display: block; width: 100%; }
                    .admin-table tr { 
                        margin-bottom: 1rem; 
                        border: 1px solid var(--border-color); 
                        border-radius: 8px; 
                        background-color: var(--card-bg);
                    }
                    .admin-table tbody tr { border-top: none; }
                    .admin-table td { 
                        text-align: right; padding-left: 50%; position: relative;
                        border-top: 1px solid var(--border-color); 
                    }
                    .admin-table tr td:first-child { border-top: none; }
                    .admin-table td::before {
                        content: attr(data-label);
                        position: absolute; left: 1.25rem; width: calc(50% - 2.5rem);
                        text-align: left; font-weight: 600; color: var(--light-text);
                        text-transform: uppercase; font-size: 0.75rem;
                    }
                    .actions-column { text-align: center; }
                    .actions-column .edit-button { width: 100%; justify-content: center; }
                    .page-header { flex-direction: column; align-items: stretch; gap: 1.5rem; }
                }
            `}</style>
        </div>
    );
}